import React, { useContext, useEffect, useState } from "react";

import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";
import LibraryAddCheckIcon from "@mui/icons-material/LibraryAddCheck";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import Card from "@mui/joy/Card";
import CardContent from "@mui/joy/CardContent";
import CardOverflow from "@mui/joy/CardOverflow";
import AspectRatio from "@mui/joy/AspectRatio";
import SpotReviewContext from "../../context/spot/spotReviewContext";
import SpotAddressLine from "../spots/SpotAddressLine";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    boxShadow: theme.shadows[10],
    padding: theme.spacing(2, 2, 2),
    width: '95vw',
    maxWidth: 680,
    height: '90vh',
    maxHeight: 600,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    outline: "none",
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(3, 4, 3),
    },
  },
}));

const PAGE_SIZE = 20;

const ExistingReviewPickerModal = ({ onPick: externalOnPick, excludeIds }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const spotContext = useContext(SpotReviewContext);
  const {
    userReviews,
    getUserReviews,
    addDraftSpotReview,
    draftSpotReviews,
    loading,
  } = spotContext;

  useEffect(() => {
    if (open) {
      getUserReviews(1, PAGE_SIZE, false);
    }
    // eslint-disable-next-line
  }, [open]);

  const handleOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };
  const handleClose = (e) => {
    if (e) e.preventDefault();
    setOpen(false);
  };

  // Ids already in the trip/draft so we don't add the same review twice.
  const takenIds = new Set([
    ...(excludeIds || []).map(String),
    ...(draftSpotReviews || [])
      .map((d) => d.existingReviewId)
      .filter((id) => id != null)
      .map(String),
  ]);

  const handlePick = (review) => {
    if (externalOnPick) {
      externalOnPick(review);
      handleClose();
      return;
    }
    // Map the server review shape onto the draft shape used by
    // DraftSpotReviews so it renders correctly in the composer.
    const visitStartMs = review.visit_from ? review.visit_from * 1000 : null;
    const durationMin =
      review.visit_from && review.visit_to && review.visit_to > review.visit_from
        ? Math.round((review.visit_to - review.visit_from) / 60)
        : null;
    addDraftSpotReview({
      existingReviewId: review.id,
      title: review.title,
      content: review.content,
      photos: review.photos || [],
      visitStartTime: visitStartMs ? new Date(visitStartMs).toISOString() : null,
      visitDuration: durationMin,
      price: review.price,
      placeTypes: review.place_types,
      category: review.category,
      placeId: review.spot_place_id,
      placeName: review.spot_name,
      placeAddress: review.spot_address,
    });
    handleClose();
  };

  return (
    <div>
      <Button
        variant="text"
        size="small"
        startIcon={<LibraryAddCheckIcon sx={{ fontSize: 16 }} />}
        onClick={handleOpen}
        sx={{
          textTransform: "none",
          fontWeight: 500,
          fontSize: "0.8125rem",
          color: "text.secondary",
          bgcolor: "background.paper",
          px: 1.5,
          py: 0.25,
          borderRadius: 999,
          border: "1px solid",
          borderColor: "divider",
          "&:hover": {
            bgcolor: "background.paper",
            color: "primary.main",
            borderColor: "primary.main",
          },
        }}
      >
        or pick from your existing reviews
      </Button>
      <Modal
        className={classes.modal}
        open={open}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={open}>
          <div className={classes.paper}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Pick one of your existing reviews
            </Typography>
            {loading && (!userReviews || userReviews.length === 0) ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : !userReviews || userReviews.length === 0 ? (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                You don't have any reviews yet.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {userReviews.map((review) => {
                  const cover =
                    (review.photos &&
                      review.photos.length > 0 &&
                      review.photos[0].image_link) ||
                    null;
                  const disabled = takenIds.has(String(review.id));
                  const visitTimeLabel = review.visit_from
                    ? new Date(review.visit_from * 1000).toLocaleDateString(
                        "en-US",
                        { month: "short", day: "numeric", year: "numeric" }
                      )
                    : null;
                  return (
                    <Card
                      key={review.id}
                      orientation="horizontal"
                      variant="outlined"
                      onClick={() => !disabled && handlePick(review)}
                      sx={{
                        cursor: disabled ? "not-allowed" : "pointer",
                        opacity: disabled ? 0.5 : 1,
                        p: 0,
                        overflow: "hidden",
                        "&:hover": disabled
                          ? {}
                          : { boxShadow: "md", transform: "translateY(-1px)" },
                      }}
                    >
                      <CardOverflow>
                        <AspectRatio
                          ratio="1"
                          sx={{ width: 96, minWidth: 96 }}
                        >
                          {cover ? (
                            <img src={cover} alt={review.title} loading="lazy" />
                          ) : (
                            <Box sx={{ bgcolor: "neutral.softBg" }} />
                          )}
                        </AspectRatio>
                      </CardOverflow>
                      <CardContent
                        sx={{ p: 1.5, display: "flex", flexDirection: "column" }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600 }}
                          noWrap
                        >
                          {review.title}
                          {disabled && (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{ color: "text.disabled", ml: 1 }}
                            >
                              (already added)
                            </Typography>
                          )}
                        </Typography>
                        {(review.spot_name || review.spot_address) && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              color: "text.secondary",
                              mt: 0.25,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <SpotAddressLine
                              placeName={review.spot_name}
                              placeAddress={review.spot_address}
                              placeId={review.spot_place_id}
                            />
                          </Typography>
                        )}
                        {visitTimeLabel && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              mt: 0.5,
                              color: "text.secondary",
                            }}
                          >
                            <AccessTimeIcon sx={{ fontSize: 14 }} />
                            <Typography variant="caption">
                              {visitTimeLabel}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}
          </div>
        </Fade>
      </Modal>
    </div>
  );
};

export default ExistingReviewPickerModal;
